import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import EdSwitch from '@/components/forms/EdSwitch'
import EdSelect from '@/components/forms/EdSelect'
import EdTextfield from '@/components/forms/EdTextfield'

interface CampoCategoria {
  id_corsi_categoria_campo: string;
  corsi_categoria_id: string;
  sort_order: string;
  tipo_campo: string;
  nome_campo: string;
  etichetta_campo: string;
  id_campo_attivato: string;
  corso_id: string;
  categoria_id: string;
  attivo: string;
}

interface CampoCorso {
  id_corso_campo: string;
  corsi_id: string;
  sort_order: string;
  tipo_campo: string;
  nome_campo: string;
  etichetta_campo: string;
}

interface DiscentiSectionProps {
  isReadOnly?: boolean;
  campiCategoria?: CampoCategoria[];
  campiCorso?: CampoCorso[];
}

const DiscentiSection = ({ isReadOnly = false, campiCategoria = [], campiCorso = [] }: DiscentiSectionProps) => {
  const { register, watch, setValue } = useFormContext();

  const selected = watch('discente') || 'campi_categoria';
  const selections = watch('discentiSelezionati') || {};

  // Stati per il form di aggiunta campo
  const [tipologia, setTipologia] = useState('');
  const [nomeEtichetta, setNomeEtichetta] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [vociTendina, setVociTendina] = useState(['']);

  // Determina quali campi mostrare in base alla selezione
  const currentItems = selected === 'campi_categoria' ? campiCategoria : campiCorso;

  // Opzioni per la dropdown tipologia
  const tipologiaOptions = [
    { value: 'text', label: 'Testo' },
    { value: 'combo', label: 'Menu a tendina' },
    { value: 'data', label: 'Data' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'file', label: 'Carica file' },
  ];

  const handleConferma = () => {
    console.log('Nuovo campo:', {
      tipologia,
      nomeEtichetta,
      descrizione,
      vociTendina: tipologia === 'combo' ? vociTendina : undefined
    });

    setTipologia('');
    setNomeEtichetta('');
    setDescrizione('');
    setVociTendina(['']);
  };

  const handleAnnulla = () => {
    setTipologia('');
    setNomeEtichetta('');
    setDescrizione('');
    setVociTendina(['']);
  };

  const handleTipologiaChange = (e: any) => {
    setTipologia(e.target.value);
  };

  const handleNomeEtichettaChange = (e: any) => {
    setNomeEtichetta(e.target.value);
  };

  const handleDescrizioneChange = (e: any) => {
    setDescrizione(e.target.value);
  };

  const handleAggiungiVoce = () => {
    setVociTendina([...vociTendina, '']);
  };

  const handleRimuoviVoce = (index: number) => {
    const newVoci = vociTendina.filter((_, i) => i !== index);
    if (newVoci.length === 0) {
      setVociTendina(['']);
    } else {
      setVociTendina(newVoci);
    }
  };

  const handleVoceChange = (index: number, value: string) => {
    const newVoci = [...vociTendina];
    newVoci[index] = value;
    setVociTendina(newVoci);
  };

  const getItemId = (item: CampoCategoria | CampoCorso) => {
    return 'id_corsi_categoria_campo' in item ? item.id_corsi_categoria_campo : item.id_corso_campo;
  };

  return (
    <div className="flex flex-col gap-4">
      <span className="text-[20px] text-[#4F4F4F] font-bold">Dati discenti al corso</span>

      {/* Radio buttons per tipo selezione */}
      <div className="flex gap-6">
        <label className={`flex items-center gap-2 ${isReadOnly ? 'cursor-not-allowed ' : 'cursor-pointer'}`}>
          <input
            type="radio"
            value="campi_categoria"
            {...register('discente')}
            disabled={isReadOnly}
            className={`w-4 h-4 text-[#B80F15] border-gray-300 focus:ring-[#B80F15] ${isReadOnly ? 'cursor-not-allowed ' : ''}`}
          />
          <span className="text-sm text-[#4F4F4F]">Campi della categoria ({campiCategoria.length})</span>
        </label>
        <label className={`flex items-center gap-2 ${isReadOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
          <input
            type="radio"
            value="campi_corso"
            {...register('discente')}
            disabled={isReadOnly}
            className={`w-4 h-4 text-[#B80F15] border-gray-300 focus:ring-[#B80F15] ${isReadOnly ? 'cursor-not-allowed opacity-50' : ''}`}
          />
          <span className="text-sm text-[#4F4F4F]">Definisci campi dedicati a questo corso ({campiCorso.length})</span>
        </label>
      </div>

      <div className="w-full">
        {currentItems.length > 0 && currentItems.map((item, idx) => {
          const itemId = getItemId(item);
          const isActive = 'attivo' in item ? item.attivo === '1' : true;
          const checked = isActive || !!selections?.[itemId];
          return (
            <div key={itemId} className={`flex items-center justify-between px-0 py-1.5 ${idx !== currentItems.length - 1 ? 'border-b border-gray-200' : ''}`}>
              <div className="flex items-center gap-12">
                <span className="text-sm text-black min-w-[180px]">{item.etichetta_campo}</span>
                <span className="text-sm text-[#6B7280]">{item.tipo_campo}</span>
              </div>
              <div className="flex items-center gap-2">
                <EdSwitch
                  id={`campo-${itemId}`}
                  value={checked}
                  onChange={() => setValue(`discentiSelezionati.${itemId}`, !checked, { shouldDirty: true })}
                  disabled={isReadOnly}
                />
                <span className={`text-sm ${checked ? 'text-black' : 'text-gray-400'}`}>Selezionato</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Form per aggiungere nuovo campo - visibile solo se è selezionato il secondo radio e non in read-only */}
      {selected === 'campi_corso' && !isReadOnly && (
        <div className="flex items-center w-full justify-between gap-4">
          <div className="flex items-center gap-4 w-full">
            <div className="flex-1 w-1/3">
              <EdSelect
                label="Tipologia campo*"
                id="tipologia"
                options={tipologiaOptions}
                value={tipologia}
                placeholder="Seleziona tipologia"
                onChange={handleTipologiaChange}
              />
            </div>

            <div className="flex-1 w-1/3 pt-[4px]">
              <EdTextfield
                label="Nome etichetta*"
                type="text"
                id="nomeEtichetta"
                placeholder="Inserisci nome etichetta"
                register={{
                  onChange: handleNomeEtichettaChange,
                  value: nomeEtichetta
                }}
              />
            </div>

            <div className="flex-1 w-1/3 pt-[4px]">
              <EdTextfield
                label="Descrizione"
                type="text"
                id="descrizione"
                placeholder="Inserisci descrizione"
                register={{
                  onChange: handleDescrizioneChange,
                  value: descrizione
                }}
              />
            </div>
          </div>

          <div className="flex gap-2 items-center">
            <button
              onClick={handleConferma}
              disabled={!tipologia || !nomeEtichetta}
              className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded hover:bg-red-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              CONFERMA
            </button>
            <button
              onClick={handleAnnulla}
              className="px-4 py-2 bg-blue-100 text-blue-700 text-sm font-medium rounded hover:bg-blue-200 transition-colors"
            >
              ANNULLA
            </button>
          </div>
        </div>
      )}

      {/* Sezione voci del menu a tendina */}
      {selected === 'campi_corso' && tipologia === 'combo' && !isReadOnly && (
        <div className="ml-4 border-l-2 border-gray-300 pl-4 mt-2">
          <div className="flex flex-col gap-3">
            {vociTendina.map((voce, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-4 h-0.5 bg-gray-300 relative -ml-4">
                  <div className="absolute -left-2 -top-1 w-1 h-1 bg-gray-300 rounded-full"></div>
                </div>
                <div className="w-[250px]">
                  <EdTextfield
                    label={index === 0 ? "Voce menu a tendina*" : ""}
                    type="text"
                    id={`voce-${index}`}
                    placeholder="Inserisci voce menu"
                    register={{
                      onChange: (e: any) => handleVoceChange(index, e.target.value),
                      value: voce
                    }}
                  />
                </div>
                <button
                  onClick={() => handleRimuoviVoce(index)}
                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                >
                  <i className="ri-delete-bin-line text-lg"></i>
                </button>
              </div>
            ))}

            <button
              onClick={handleAggiungiVoce}
              className="text-red-600 hover:text-red-800 text-sm font-medium self-start transition-colors"
            >
              + Aggiungi nuova voce
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DiscentiSection
